/*
 * PRD-037 — Pantalla de login del portal de partners (pre-auth, fuera del PartnerLayout).
 * Shell propio centrado vertical/horizontalmente con branding; consume los primitivos canónicos
 * (Card / Field / Button / ErrorNote) del módulo `ui`.
 */
import { useState, type FormEvent } from 'react';
import { partnerLogin } from '@services/partnerService';
import { usePartnerAuth } from '../PartnerAuthContext';
import { useDesktop } from '../../utils/hooks/useDesktop';
import { Button, Card, ErrorNote, Field, C } from '../ui';

const PartnerLogin = () => {
  const { login } = usePartnerAuth();
  const isDesktop = useDesktop();
  const [usuario, setUsuario] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const session = await partnerLogin(usuario.trim(), password);
      login(session.token, session.partner);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 403) setError('Tu cuenta no tiene el módulo de onboarding de partners.');
      else if (status === 401) setError('Usuario o contraseña inválidos.');
      else setError('No se pudo iniciar sesión. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: C.pageBg,
        fontFamily: 'Poppins, sans-serif',
        color: C.primary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isDesktop ? '32px 24px' : '24px 16px',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ width: '100%', maxWidth: 400, margin: '0 auto' }}>
        {/* Branding */}
        <div style={{ textAlign: 'center', marginBottom: 22 }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 54,
              height: 54,
              borderRadius: 15,
              background: C.primary,
              color: C.white,
              fontWeight: 700,
              fontSize: 28,
              lineHeight: 1,
              marginBottom: 12,
            }}
            aria-hidden="true"
          >
            P
          </span>
          <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.01em' }}>
            Plick <span style={{ color: C.secondary }}>Partners</span>
          </div>
          <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>Portal de staff</div>
        </div>

        <Card style={{ marginBottom: 14 }}>
          <h1 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 4px', lineHeight: 1.3 }}>Inicia sesión</h1>
          <p style={{ fontSize: 13, color: C.muted, margin: '0 0 18px' }}>
            Usa tu cuenta de staff para pre-registrar comercios.
          </p>
          <form onSubmit={onSubmit}>
            <Field label="Usuario" value={usuario} onChange={setUsuario} autoComplete="username" required />
            <Field
              label="Contraseña"
              type="password"
              value={password}
              onChange={setPassword}
              autoComplete="current-password"
              required
            />
            {error && <ErrorNote>{error}</ErrorNote>}
            <Button type="submit" fullWidth loading={loading} style={{ marginTop: 18 }}>
              Entrar
            </Button>
          </form>
        </Card>

        <p style={{ fontSize: 12, color: C.muted, textAlign: 'center', margin: 0 }}>
          Acceso exclusivo para staff de Plick.
        </p>
      </div>
    </div>
  );
};

export default PartnerLogin;
